import React, { useState, useEffect } from 'react';
import { DemographicAnalyticsData, DemographicTurnout, HourlyTurnout, RoleDemographicBreakdown } from '../types';
import {
  BarChart2,
  BarChart3,
  PieChart,
  Table as TableIcon,
  Download,
  Users,
  TrendingUp,
  Award,
  Layers,
  CheckCircle2,
  ChevronDown,
  LayoutGrid,
  Clock,
  Flame,
  Zap,
  Activity,
  Link as LinkIcon,
  Check,
  Share2,
} from 'lucide-react';

export type ChartFormat = 'horizontal' | 'vertical' | 'donut' | 'table';
export type RoleDimension = 'level' | 'gender' | 'hall';
export type RoleViewMode = 'cards' | 'matrix' | 'stacked';

interface DemographicResultsSectionProps {
  analytics: DemographicAnalyticsData | null;
  loading: boolean;
  electionTitle?: string;
  isAdmin?: boolean;
}

// Harmonious design system palettes
const LEVEL_COLORS = ['#FF9900', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B'];
const GENDER_COLORS: Record<string, string> = {
  Female: '#EC4899',
  female: '#EC4899',
  Male: '#3B82F6',
  male: '#3B82F6',
  Unspecified: '#9CA3AF',
};
const HALL_COLORS = [
  '#FF9900', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', 
  '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'
];
const CANDIDATE_PALETTE = [
  '#FF9900', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4', '#F59E0B'
];

export const DemographicResultsSection: React.FC<DemographicResultsSectionProps> = ({
  analytics,
  loading,
  electionTitle,
  isAdmin = false,
}) => {
  // Global format and dimension filters
  const [chartFormat, setChartFormat] = useState<ChartFormat>('horizontal');
  const [activeDimension, setActiveDimension] = useState<'all' | 'hourly' | 'level' | 'gender' | 'hall'>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const dim = params.get('dimension');
      if (dim && ['all', 'hourly', 'level', 'gender', 'hall'].includes(dim)) {
        return dim as any;
      }
    } catch {}
    return 'all';
  });

  // Direct Link feedback state
  const [copiedLink, setCopiedLink] = useState(false);
  const handleCopyDirectLink = () => {
    const origin = window.location.origin;
    let url = '';
    const isCurrentlyAdmin = isAdmin || window.location.pathname.startsWith('/admin');
    if (isCurrentlyAdmin) {
      url = `${origin}/admin/dashboard?tab=analytics${activeDimension !== 'all' ? `&dimension=${activeDimension}` : ''}`;
    } else {
      url = `${origin}/results/demographics${activeDimension !== 'all' ? `?dimension=${activeDimension}` : ''}`;
    }
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    });
  };

  // Role-by-role demographic inspection state
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [roleDimension, setRoleDimension] = useState<RoleDimension>('level');
  const [roleViewMode, setRoleViewMode] = useState<RoleViewMode>('matrix');

  if (loading && !analytics) {
    return (
      <div style={{ padding: '80px', textAlign: 'center', color: '#8E8E93', fontSize: '16px' }}>
        Loading demographic participation intelligence...
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
        No demographic participation data available yet.
      </div>
    );
  }

  // Calculate summary metrics
  const totalAccredited = analytics.byLevel.reduce((sum, item) => sum + item.accredited, 0);
  const totalVoted = analytics.byLevel.reduce((sum, item) => sum + item.voted, 0);
  const overallTurnout = totalAccredited > 0 ? ((totalVoted / totalAccredited) * 100).toFixed(1) : '0.0';

  const highestLevel = [...analytics.byLevel].sort((a, b) => b.percentage - a.percentage)[0];

  // Hourly turnout calculations
  const hourlyData: HourlyTurnout[] = analytics.byHour && analytics.byHour.length > 0 ? analytics.byHour : [];
  const peakHour = hourlyData.find((h) => h.isPeak) || [...hourlyData].sort((a, b) => b.count - a.count)[0];
  const totalVotesAcrossHours = hourlyData.reduce((sum, h) => sum + h.count, 0);
  const activeHoursCount = hourlyData.filter((h) => h.count > 0).length || hourlyData.length || 1;
  const avgVotesPerHour = (totalVotesAcrossHours / activeHoursCount).toFixed(1);

  // Resolve current active role
  const roles = analytics.byRole || [];
  const currentRole = roles.find((r) => r.positionId === selectedRoleId) || roles[0] || null;

  // CSV Export handler
  const handleExportCsv = () => {
    const rows = [
      ['Dimension', 'Group / Cohort', 'Accredited Voters', 'Votes Cast', 'Turnout Percentage'],
      ...analytics.byLevel.map((r) => ['Academic Level', r.group, r.accredited, r.voted, `${r.percentage}%`]),
      ...analytics.byGender.map((r) => ['Gender', r.group, r.accredited, r.voted, `${r.percentage}%`]),
      ...analytics.byHall.map((r) => ['Hall of Residence', r.group, r.accredited, r.voted, `${r.percentage}%`]),
    ];

    if (hourlyData.length > 0) {
      rows.push(['']);
      rows.push(['--- HOURLY PARTICIPATION BREAKDOWN ---']);
      rows.push(['Time Window', 'Votes Cast This Hour', 'Cumulative Votes', 'Share of Total (%)', 'Peak Hour Status']);
      hourlyData.forEach((h) => {
        rows.push([h.hour, String(h.count), String(h.cumulative), `${h.percentage}%`, h.isPeak ? 'YES (PEAK)' : 'NO']);
      });
    }

    if (roles.length > 0) {
      rows.push(['']);
      rows.push(['--- ROLE DEMOGRAPHIC CROSS-TABULATION ---']);
      rows.push(['Role', 'Candidate', 'Segment Dimension', 'Segment Name', 'Votes Received']);
      roles.forEach((pos) => {
        pos.candidates.forEach((cand) => {
          cand.byLevel.forEach((l) => {
            rows.push([pos.positionName, cand.name, 'Academic Level', l.segment, String(l.votes)]);
          });
          cand.byGender.forEach((g) => {
            rows.push([pos.positionName, cand.name, 'Gender', g.segment, String(g.votes)]);
          });
          cand.byHall.forEach((h) => {
            rows.push([pos.positionName, cand.name, 'Hall of Residence', h.segment, String(h.votes)]);
          });
        });
      });
    }

    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map((e) => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `demographic-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper: Render Donut Chart
  const renderDonutChart = (items: DemographicTurnout[], colors: string[], title: string) => {
    const totalVotes = items.reduce((sum, i) => sum + i.voted, 0);
    const size = 220;
    const strokeWidth = 28;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    let accumulatedPercent = 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
        <div style={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke="#F3F4F6"
              strokeWidth={strokeWidth}
            />
            {items.map((item, idx) => {
              if (totalVotes === 0 || item.voted === 0) return null;
              const percent = item.voted / totalVotes;
              const strokeDasharray = `${circumference * percent} ${circumference * (1 - percent)}`;
              const strokeDashoffset = -circumference * accumulatedPercent;
              accumulatedPercent += percent;
              const strokeColor = colors[idx % colors.length];

              return (
                <circle
                  key={item.group}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="transparent"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 0.5s ease' }}
                />
              );
            })}
          </svg>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#171619', fontFamily: 'Syne, sans-serif' }}>
              {totalVotes}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Cast
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map((item, idx) => {
            const color = colors[idx % colors.length];
            const share = totalVotes > 0 ? ((item.voted / totalVotes) * 100).toFixed(1) : '0.0';
            return (
              <div
                key={item.group}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '12px',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: color }} />
                  <span style={{ fontWeight: 600, color: '#1F2937' }}>{item.group}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ color: '#6B7280' }}>
                    {item.voted} / {item.accredited} ({item.percentage}%)
                  </span>
                  <span style={{ fontWeight: 700, color: '#111827', width: '48px', textAlign: 'right' }}>
                    {share}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper: Render Vertical Column Chart
  const renderVerticalColumnChart = (items: DemographicTurnout[], colors: string[]) => {
    const maxPercent = Math.max(...items.map((i) => i.percentage), 20);

    return (
      <div style={{ paddingTop: '20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-around',
            height: '240px',
            borderBottom: '2px solid #E5E7EB',
            paddingBottom: '8px',
            gap: '12px',
          }}
        >
          {items.map((item, idx) => {
            const heightPercent = maxPercent > 0 ? Math.max((item.percentage / maxPercent) * 100, 4) : 4;
            const barColor = colors[idx % colors.length];

            return (
              <div
                key={item.group}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
                  {item.percentage}%
                </div>
                <div
                  style={{
                    width: '80%',
                    maxWidth: '48px',
                    height: `${heightPercent}%`,
                    backgroundColor: barColor,
                    borderRadius: '8px 8px 0 0',
                    transition: 'height 0.5s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  title={`${item.group}: ${item.voted} / ${item.accredited} (${item.percentage}%)`}
                />
              </div>
            );
          })}
        </div>

        {/* Labels below */}
        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '12px', gap: '12px' }}>
          {items.map((item) => (
            <div
              key={item.group}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: 600,
                color: '#4B5563',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={item.group}
            >
              {item.group.replace(' Level', 'L')}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper: Render Horizontal Bar Chart
  const renderHorizontalBarChart = (items: DemographicTurnout[], colors: string[]) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {items.map((item, idx) => {
          const color = colors[idx % colors.length];
          return (
            <div key={item.group} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937' }}>
                  {item.group}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>
                    {item.voted} / {item.accredited}
                  </span>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: color,
                      backgroundColor: `${color}15`,
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }}
                  >
                    {item.percentage}%
                  </span>
                </div>
              </div>
              <div
                style={{
                  height: '10px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '999px',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(item.percentage, 100)}%`,
                    backgroundColor: color,
                    borderRadius: '999px',
                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Helper: Render Tabular Data View
  const renderTableView = (items: DemographicTurnout[], colors: string[]) => {
    return (
      <div>
        <div className="mobile-table-swipe-hint">
          <span>↔ Swipe horizontally to inspect complete cohort breakdown</span>
        </div>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px', border: '1px solid #F3F4F6' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB', color: '#6B7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                <th style={{ padding: '12px 14px', position: 'sticky', left: 0, backgroundColor: '#F9FAFB', zIndex: 3, borderRight: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>Cohort / Group</th>
                <th style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>Accredited</th>
                <th style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>Ballots Cast</th>
                <th style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>Turnout Rate</th>
                <th style={{ padding: '12px 14px', minWidth: '140px', whiteSpace: 'nowrap' }}>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const color = colors[idx % colors.length];
                return (
                  <tr key={item.group} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#111827', position: 'sticky', left: 0, backgroundColor: '#FFFFFF', zIndex: 2, borderRight: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                        {item.group}
                      </div>
                    </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: '#4B5563' }}>
                    {item.accredited}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, color: '#111827' }}>
                    {item.voted}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '999px',
                        backgroundColor: `${color}18`,
                        color: color,
                        fontWeight: 700,
                        fontSize: '12px',
                      }}
                    >
                      {item.percentage}%
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ height: '6px', backgroundColor: '#F3F4F6', borderRadius: '999px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: color,
                          borderRadius: '999px',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  const renderSectionContent = (items: DemographicTurnout[], colors: string[], title: string) => {
    switch (chartFormat) {
      case 'vertical':
        return renderVerticalColumnChart(items, colors);
      case 'donut':
        return renderDonutChart(items, colors, title);
      case 'table':
        return renderTableView(items, colors);
      case 'horizontal':
      default:
        return renderHorizontalBarChart(items, colors);
    }
  };

  // Helper: Render Hourly Section based on chosen format
  const renderHourlySection = (format: ChartFormat) => {
    if (hourlyData.length === 0 || totalVotesAcrossHours === 0) {
      return (
        <div style={{ padding: '36px 20px', textAlign: 'center', backgroundColor: '#F9FAFB', borderRadius: '12px', border: '1px dashed #E5E7EB' }}>
          <Clock size={28} color="#9CA3AF" style={{ margin: '0 auto 8px auto', display: 'block' }} />
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
            No Hourly Voting Activity Recorded Yet
          </div>
          <p style={{ fontSize: '12.5px', color: '#6B7280', margin: 0, maxWidth: '440px', marginLeft: 'auto', marginRight: 'auto' }}>
            When accredited voters begin casting ballots during the election window, live votes per hour and turnout velocity will appear here automatically across all visual formats.
          </p>
        </div>
      );
    }

    const maxCount = Math.max(...hourlyData.map((h) => h.count), 1);

    if (format === 'vertical') {
      return (
        <div style={{ paddingTop: '20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              height: '240px',
              borderBottom: '2px solid #E5E7EB',
              paddingBottom: '8px',
              gap: '8px',
            }}
          >
            {hourlyData.map((item) => {
              const heightPercent = maxCount > 0 ? Math.max((item.count / maxCount) * 100, 4) : 4;
              const isPeak = item.isPeak || (item.count > 0 && item.count === maxCount);

              return (
                <div
                  key={item.hour}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    justifyContent: 'flex-end',
                    position: 'relative',
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 700, color: isPeak ? '#FF9900' : '#111827', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {isPeak && <Flame size={12} color="#FF9900" />}
                    <span>{item.count}</span>
                  </div>
                  <div
                    style={{
                      width: '80%',
                      maxWidth: '44px',
                      height: `${heightPercent}%`,
                      background: isPeak
                        ? 'linear-gradient(180deg, #FF9900 0%, #D97706 100%)'
                        : 'linear-gradient(180deg, #3B82F6 0%, #2563EB 100%)',
                      borderRadius: '8px 8px 0 0',
                      transition: 'height 0.5s ease',
                      boxShadow: isPeak ? '0 4px 12px rgba(255, 153, 0, 0.35)' : '0 2px 6px rgba(0,0,0,0.06)',
                      border: isPeak ? '1.5px solid #FF9900' : 'none',
                    }}
                    title={`${item.hour}: ${item.count} votes (${item.percentage}%) | Cumulative: ${item.cumulative}`}
                  />
                </div>
              );
            })}
          </div>

          {/* Labels & cumulative under */}
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '12px', gap: '8px' }}>
            {hourlyData.map((item) => (
              <div
                key={item.hour}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: item.isPeak ? '#FF9900' : '#4B5563',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={item.hour}
              >
                {item.hour.replace(':00', '')}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (format === 'horizontal') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {hourlyData.map((item) => {
            const isPeak = item.isPeak || (item.count > 0 && item.count === maxCount);
            return (
              <div key={item.hour} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#1F2937' }}>
                      {item.hour}
                    </span>
                    {isPeak && (
                      <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', backgroundColor: 'rgba(255, 153, 0, 0.15)', color: '#FF9900', border: '1px solid rgba(255, 153, 0, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <Flame size={10} /> Peak Hour
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '12.5px', color: '#6B7280', fontWeight: 500 }}>
                      {item.count} votes ({item.percentage}%)
                    </span>
                    <span style={{ fontSize: '11.5px', color: '#9CA3AF', fontWeight: 600 }}>
                      Cum: {item.cumulative}
                    </span>
                  </div>
                </div>
                <div style={{ width: '100%', height: '10px', backgroundColor: '#F3F4F6', borderRadius: '6px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%`,
                      background: isPeak ? 'linear-gradient(90deg, #FF9900, #F59E0B)' : 'linear-gradient(90deg, #3B82F6, #60A5FA)',
                      borderRadius: '6px',
                      transition: 'width 0.5s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (format === 'donut') {
      const demographicItems: DemographicTurnout[] = hourlyData.map((h) => ({
        group: h.hour,
        accredited: totalVotesAcrossHours,
        voted: h.count,
        percentage: h.percentage,
      }));
      return renderDonutChart(demographicItems, HALL_COLORS, 'Hourly Participation');
    }

    // Default: 'table'
    return (
      <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              <th style={{ padding: '12px 18px', fontWeight: 700, color: '#374151' }}>Time Window</th>
              <th style={{ padding: '12px 18px', fontWeight: 700, color: '#374151' }}>Votes Cast</th>
              <th style={{ padding: '12px 18px', fontWeight: 700, color: '#374151' }}>Turnout Share</th>
              <th style={{ padding: '12px 18px', fontWeight: 700, color: '#374151' }}>Cumulative Ballots</th>
              <th style={{ padding: '12px 18px', fontWeight: 700, color: '#374151', textAlign: 'right' }}>Activity Level</th>
            </tr>
          </thead>
          <tbody>
            {hourlyData.map((item, idx) => {
              const isPeak = item.isPeak || (item.count > 0 && item.count === maxCount);
              return (
                <tr key={item.hour} style={{ borderBottom: idx < hourlyData.length - 1 ? '1px solid #F3F4F6' : 'none', backgroundColor: isPeak ? 'rgba(255, 153, 0, 0.04)' : 'transparent' }}>
                  <td style={{ padding: '12px 18px', fontWeight: 600, color: '#111827' }}>
                    {item.hour}
                  </td>
                  <td style={{ padding: '12px 18px', fontWeight: 700, color: isPeak ? '#FF9900' : '#1F2937' }}>
                    {item.count}
                  </td>
                  <td style={{ padding: '12px 18px', color: '#4B5563' }}>
                    {item.percentage}%
                  </td>
                  <td style={{ padding: '12px 18px', color: '#6B7280' }}>
                    {item.cumulative}
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                    {isPeak ? (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', backgroundColor: 'rgba(255, 153, 0, 0.15)', padding: '3px 8px', borderRadius: '10px' }}>
                        🔥 Peak Hour
                      </span>
                    ) : item.count > 0 ? (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#10B981', backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '3px 8px', borderRadius: '10px' }}>
                        Active
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Quiet</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const formatButtons: Array<{ id: ChartFormat; label: string; icon: React.FC<{ size?: number }> }> = [
    { id: 'horizontal', label: 'Horizontal Bars', icon: BarChart2 },
    { id: 'vertical', label: 'Columns', icon: BarChart3 },
    { id: 'donut', label: 'Donut Chart', icon: PieChart },
    { id: 'table', label: 'Data Table', icon: TableIcon },
  ];

  // ===========================================================================
  // ROLE DEMOGRAPHIC BREAKDOWN RENDERERS
  // ===========================================================================
  const renderRoleBreakdown = () => {
    if (!currentRole || currentRole.candidates.length === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
          No candidate voting breakdown recorded for this role yet.
        </div>
      );
    }

    // Determine segments based on chosen dimension
    let segments: string[] = [];
    if (roleDimension === 'level') {
      segments = ['100 Level', '200 Level', '300 Level', '400 Level', '500 Level'];
    } else if (roleDimension === 'gender') {
      segments = ['Female', 'Male'];
    } else {
      // Collect unique halls
      const hallSet = new Set<string>();
      currentRole.candidates.forEach((c) => {
        c.byHall.forEach((h) => hallSet.add(h.segment));
      });
      segments = Array.from(hallSet);
      if (segments.length === 0) segments = ['Independence Hall', 'Queen Idia Hall', 'Queen Elizabeth II Hall', 'Sultan Bello Hall'];
    }

    // Helper to get candidate votes in a segment
    const getCandVotes = (cand: typeof currentRole.candidates[0], seg: string): number => {
      let match;
      if (roleDimension === 'level') {
        match = cand.byLevel.find((r) => r.segment === seg || r.segment === seg.replace(' Level', ''));
      } else if (roleDimension === 'gender') {
        match = cand.byGender.find((r) => r.segment.toLowerCase() === seg.toLowerCase());
      } else {
        match = cand.byHall.find((r) => r.segment.toLowerCase() === seg.toLowerCase());
      }
      return match ? match.votes : 0;
    };

    // Calculate total votes cast in this role
    const totalRoleVotes = currentRole.candidates.reduce((sum, c) => sum + c.totalVotes, 0);

    // --- Mode 1: Cross-Tabulation Matrix (Table) ---
    if (roleViewMode === 'matrix') {
      return (
        <div>
          <div className="mobile-table-swipe-hint">
            <span>↔ Swipe horizontally to inspect all candidates & winner</span>
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px', border: '1px solid #F3F4F6' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB', color: '#6B7280', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '12px 14px', position: 'sticky', left: 0, backgroundColor: '#F9FAFB', zIndex: 3, borderRight: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>
                    Demographic Cohort
                  </th>
                  {currentRole.candidates.map((c, cIdx) => (
                    <th key={c.id} style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: CANDIDATE_PALETTE[cIdx % CANDIDATE_PALETTE.length], flexShrink: 0 }} />
                        <span>
                          {c.name}
                          {c.nickname && (
                            <span style={{ color: '#FF9900', fontSize: '11px', marginLeft: '4px', fontWeight: 700 }}>
                              "{c.nickname}"
                            </span>
                          )}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>Cohort Winner</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>Total Ballots</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((seg) => {
                  const votesByCand = currentRole.candidates.map((c) => ({
                    cand: c,
                    votes: getCandVotes(c, seg),
                  }));
                  const segTotal = votesByCand.reduce((sum, item) => sum + item.votes, 0);
                  const winner = [...votesByCand].sort((a, b) => b.votes - a.votes)[0];
                  const isWinnerTie = segTotal > 0 && votesByCand.filter((v) => v.votes === winner.votes).length > 1;

                  return (
                    <tr key={seg} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: '#111827', position: 'sticky', left: 0, backgroundColor: '#FFFFFF', zIndex: 2, borderRight: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                        {seg}
                      </td>
                    {votesByCand.map(({ cand, votes }, cIdx) => {
                      const share = segTotal > 0 ? ((votes / segTotal) * 100).toFixed(1) : '0.0';
                      const color = CANDIDATE_PALETTE[cIdx % CANDIDATE_PALETTE.length];
                      const isLeadingHere = segTotal > 0 && votes === winner.votes && !isWinnerTie && !cand.isAbstain;

                      return (
                        <td key={cand.id} style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, color: isLeadingHere ? '#FF9900' : '#111827' }}>
                              {votes}
                            </span>
                            <span style={{ fontSize: '11px', color: '#6B7280' }}>
                              ({share}%)
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {segTotal === 0 ? (
                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>—</span>
                      ) : isWinnerTie ? (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280' }}>Tie</span>
                      ) : (
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: '999px',
                            backgroundColor: 'rgba(255, 153, 0, 0.1)',
                            color: '#FF9900',
                            fontWeight: 700,
                            fontSize: '12px',
                          }}
                        >
                          {winner.cand.name}{winner.cand.nickname && ` "${winner.cand.nickname}"`}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4B5563' }}>
                      {segTotal}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

    // --- Mode 2: Candidate Segment Breakdown Cards ---
    if (roleViewMode === 'cards') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {currentRole.candidates.map((cand, cIdx) => {
            const candColor = CANDIDATE_PALETTE[cIdx % CANDIDATE_PALETTE.length];
            const candShare = totalRoleVotes > 0 ? ((cand.totalVotes / totalRoleVotes) * 100).toFixed(1) : '0.0';

            return (
              <div
                key={cand.id}
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '16px',
                  border: '1.5px solid #F3F4F6',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {/* Candidate Mini Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: candColor, flexShrink: 0 }} />
                    <div>
                      <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827', fontFamily: 'Syne, sans-serif', display: 'block', lineHeight: 1.3 }}>
                        {cand.name}
                      </span>
                      {cand.nickname && (
                        <div style={{ color: '#FF9900', fontSize: '11.5px', fontWeight: 700, marginTop: '2px', lineHeight: 1.3 }}>
                          "{cand.nickname}"
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: '#171619' }}>
                      {cand.totalVotes} <span style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>Votes</span>
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: candColor }}>
                      {candShare}% of Role
                    </div>
                  </div>
                </div>

                {/* Segment breakdown for this candidate */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {segments.map((seg) => {
                    const votes = getCandVotes(cand, seg);
                    const pct = cand.totalVotes > 0 ? ((votes / cand.totalVotes) * 100).toFixed(1) : '0.0';

                    return (
                      <div key={seg} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <span style={{ color: '#4B5563', fontWeight: 600 }}>{seg}</span>
                          <span style={{ color: '#111827', fontWeight: 700 }}>
                            {votes} <span style={{ color: '#6B7280', fontWeight: 400 }}>({pct}%)</span>
                          </span>
                        </div>
                        <div style={{ height: '6px', backgroundColor: '#E5E7EB', borderRadius: '999px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${Math.min(parseFloat(pct), 100)}%`,
                              backgroundColor: candColor,
                              borderRadius: '999px',
                              transition: 'width 0.5s ease',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // --- Mode 3: 100% Stacked Cohort Distribution ---
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Color Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' }}>
          {currentRole.candidates.map((c, cIdx) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: CANDIDATE_PALETTE[cIdx % CANDIDATE_PALETTE.length] }} />
              <span style={{ fontWeight: 600, color: '#1F2937' }}>
                {c.name}
                {c.nickname && (
                  <span style={{ color: '#FF9900', fontSize: '11px', marginLeft: '4px', fontWeight: 700 }}>
                    "{c.nickname}"
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Stacked Bars per Segment */}
        {segments.map((seg) => {
          const votesByCand = currentRole.candidates.map((c, cIdx) => ({
            cand: c,
            votes: getCandVotes(c, seg),
            color: CANDIDATE_PALETTE[cIdx % CANDIDATE_PALETTE.length],
          }));
          const segTotal = votesByCand.reduce((sum, item) => sum + item.votes, 0);

          return (
            <div key={seg} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ fontWeight: 700, color: '#111827' }}>{seg}</span>
                <span style={{ color: '#6B7280', fontSize: '12px' }}>{segTotal} Votes Cast</span>
              </div>
              <div style={{ height: '22px', backgroundColor: '#F3F4F6', borderRadius: '8px', overflow: 'hidden', display: 'flex', width: '100%' }}>
                {segTotal === 0 ? (
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#F3F4F6' }} />
                ) : (
                  votesByCand.map(({ cand, votes, color }) => {
                    const widthPercent = (votes / segTotal) * 100;
                    if (widthPercent === 0) return null;
                    return (
                      <div
                        key={cand.id}
                        style={{
                          width: `${widthPercent}%`,
                          height: '100%',
                          backgroundColor: color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                          fontSize: '11px',
                          fontWeight: 700,
                          transition: 'width 0.5s ease',
                        }}
                        title={`${cand.name}: ${votes} votes (${widthPercent.toFixed(1)}%)`}
                      >
                        {widthPercent >= 12 ? `${widthPercent.toFixed(0)}%` : ''}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
      {/* 3 Top Summary Metrics */}
      <div className="demographic-summary-grid">
        <div className="demographic-summary-card">
          <div
            className="summary-icon-box"
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '12px',
              backgroundColor: '#FFF7ED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FF9900',
              flexShrink: 0,
            }}
          >
            <Users size={22} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Accredited Roster
            </div>
            <div className="summary-val" style={{ fontSize: '24px', fontWeight: 800, color: '#171619', fontFamily: 'Syne, sans-serif' }}>
              {totalAccredited} <span style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>Students</span>
            </div>
          </div>
        </div>

        <div className="demographic-summary-card">
          <div
            className="summary-icon-box"
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '12px',
              backgroundColor: '#ECFDF5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10B981',
              flexShrink: 0,
            }}
          >
            <TrendingUp size={22} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Turnout Rate
            </div>
            <div className="summary-val" style={{ fontSize: '24px', fontWeight: 800, color: '#10B981', fontFamily: 'Syne, sans-serif' }}>
              {overallTurnout}% <span style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>({totalVoted} Voted)</span>
            </div>
          </div>
        </div>

        {highestLevel && (
          <div className="demographic-summary-card">
            <div
              className="summary-icon-box"
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                backgroundColor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#3B82F6',
                flexShrink: 0,
              }}
            >
              <Award size={22} />
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Leading Cohort
              </div>
              <div className="summary-val" style={{ fontSize: '20px', fontWeight: 800, color: '#171619', fontFamily: 'Syne, sans-serif' }}>
                {highestLevel.group}{' '}
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#3B82F6' }}>
                  ({highestLevel.percentage}%)
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Interactive Demographic Control Deck */}
      <div className="demographic-controls-deck">
        {/* Tier 1: Demographic Dimension Tabs & Primary Actions */}
        <div className="demographic-deck-row demographic-deck-primary">
          <div className="demographic-deck-group">
            <span className="demographic-deck-label">
              Demographic View:
            </span>
            <div className="demographic-scroll-pills">
              {[
                { id: 'all', label: 'All Demographics' },
                { id: 'hourly', label: 'Votes Per Hour ⏱' },
                { id: 'level', label: 'By Level' },
                { id: 'gender', label: 'By Gender' },
                { id: 'hall', label: 'By Hall' },
              ].map((dim) => {
                const isActive = activeDimension === dim.id;
                return (
                  <button
                    key={dim.id}
                    type="button"
                    onClick={() => setActiveDimension(dim.id as any)}
                    className="demographic-pill-btn"
                    style={{
                      padding: '7px 14px',
                      borderRadius: '9px',
                      backgroundColor: isActive ? '#171619' : 'transparent',
                      color: isActive ? '#FF9900' : '#4B5563',
                      fontWeight: 700,
                      fontSize: '12.5px',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {dim.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Buttons: Direct Link & CSV Export */}
          <div className="demographic-deck-actions">
            <button
              type="button"
              onClick={handleCopyDirectLink}
              className="demographic-action-btn-secondary"
              title="Copy direct shareable link to this Demographic Analytics subpage"
            >
              {copiedLink ? <Check size={14} color="#10B981" /> : <LinkIcon size={14} />}
              <span>{copiedLink ? 'Link Copied!' : 'Direct Link'}</span>
            </button>

            <button
              type="button"
              onClick={handleExportCsv}
              className="demographic-action-btn-primary"
              title="Download full demographic turnout breakdown as CSV"
            >
              <Download size={14} />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Tier 2: Visualization Format Controls & Current View Context */}
        <div className="demographic-deck-row demographic-deck-secondary">
          <div className="demographic-deck-group">
            <span className="demographic-deck-label">
              Chart Format:
            </span>
            <div className="demographic-scroll-pills">
              {formatButtons.map((btn) => {
                const Icon = btn.icon;
                const isActive = chartFormat === btn.id;
                return (
                  <button
                    key={btn.id}
                    type="button"
                    onClick={() => setChartFormat(btn.id)}
                    className="demographic-pill-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                      color: isActive ? '#171619' : '#6B7280',
                      fontWeight: 700,
                      fontSize: '12px',
                      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon size={14} />
                    <span>{btn.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* View Scope Indicator */}
          <div className="demographic-view-indicator">
            <span className="indicator-dot" />
            <span>
              {activeDimension === 'hourly'
                ? `Hourly Turnout Timeline • ${hourlyData.length} Active Intervals`
                : activeDimension === 'all'
                ? `Full Demographic Intelligence • Hourly, Level, Gender & Hall`
                : `Demographic Cohort: ${activeDimension.toUpperCase()}`}
            </span>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* 1. OVERALL TURNOUT DEMOGRAPHIC CARDS (STACKED ON EACH OTHER)          */}
      {/* ===================================================================== */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {/* Hourly Turnout Card */}
        {(activeDimension === 'all' || activeDimension === 'hourly') && (
          <div className="results-position-card">
            <div className="results-pos-header">
              <div className="results-pos-title-group">
                <h2 className="results-pos-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Votes Cast Per Hour (Hourly Voting Timeline & Velocity)</span>
                </h2>
                <span className="results-pos-type">
                  Hourly participation progression across the election voting window
                </span>
              </div>
              <div
                className="results-pos-badge"
                style={{
                  color: '#3B82F6',
                  backgroundColor: '#EFF6FF',
                }}
              >
                <Clock size={13} />
                <span>{hourlyData.length} Time Slots</span>
              </div>
            </div>

            {/* Quick summary metrics chips for hourly */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div style={{ padding: '12px 14px', borderRadius: '10px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '11.5px', color: '#6B7280', display: 'block', fontWeight: 600 }}>Peak Voting Hour</span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#FF9900', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                  <Flame size={14} /> {peakHour && peakHour.count > 0 ? `${peakHour.hour} (${peakHour.count} votes)` : 'None yet'}
                </span>
              </div>
              <div style={{ padding: '12px 14px', borderRadius: '10px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '11.5px', color: '#6B7280', display: 'block', fontWeight: 600 }}>Hourly Average</span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827', marginTop: '2px', display: 'block' }}>
                  {avgVotesPerHour} votes / hr
                </span>
              </div>
              <div style={{ padding: '12px 14px', borderRadius: '10px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '11.5px', color: '#6B7280', display: 'block', fontWeight: 600 }}>Total Hourly Ballots</span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#10B981', marginTop: '2px', display: 'block' }}>
                  {totalVotesAcrossHours} verified
                </span>
              </div>
            </div>

            {renderHourlySection(chartFormat)}
          </div>
        )}

        {/* 1. Academic Level Card */}
        {(activeDimension === 'all' || activeDimension === 'level') && (
          <div className="results-position-card">
            <div className="results-pos-header">
              <div className="results-pos-title-group">
                <h2 className="results-pos-title">
                  Voter Turnout by Academic Level
                </h2>
                <span className="results-pos-type">
                  Undergraduate class cohort breakdown (100L through 500L)
                </span>
              </div>
              <div
                className="results-pos-badge"
                style={{
                  color: '#FF9900',
                  backgroundColor: '#FFF7ED',
                }}
              >
                <Layers size={13} />
                <span>5 Cohorts</span>
              </div>
            </div>

            {renderSectionContent(analytics.byLevel, LEVEL_COLORS, 'Academic Level Turnout')}
          </div>
        )}

        {/* 2. Turnout by Gender Card (Now Stacked) */}
        {(activeDimension === 'all' || activeDimension === 'gender') && (
          <div className="results-position-card">
            <div className="results-pos-header">
              <div className="results-pos-title-group">
                <h2 className="results-pos-title">
                  Turnout by Gender
                </h2>
                <span className="results-pos-type">
                  Electorate participation distribution
                </span>
              </div>
              <div
                className="results-pos-badge"
                style={{
                  color: '#EC4899',
                  backgroundColor: '#FDF2F8',
                }}
              >
                <Users size={13} />
                <span>Gender Distribution</span>
              </div>
            </div>

            {renderSectionContent(
              analytics.byGender,
              analytics.byGender.map((g) => GENDER_COLORS[g.group] || '#3B82F6'),
              'Gender Participation'
            )}
          </div>
        )}

        {/* 3. Hall of Residence Rankings Card (Now Stacked) */}
        {(activeDimension === 'all' || activeDimension === 'hall') && (
          <div className="results-position-card">
            <div className="results-pos-header">
              <div className="results-pos-title-group">
                <h2 className="results-pos-title">
                  Hall of Residence Rankings
                </h2>
                <span className="results-pos-type">
                  Student resident hall participation rankings
                </span>
              </div>
              <div
                className="results-pos-badge"
                style={{
                  color: '#10B981',
                  backgroundColor: '#ECFDF5',
                }}
              >
                <Award size={13} />
                <span>Rankings</span>
              </div>
            </div>

            {renderSectionContent(analytics.byHall, HALL_COLORS, 'Hall Participation')}
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* 2. ROLE-BY-ROLE DEMOGRAPHIC VOTING BREAKDOWN (AT THE BOTTOM OF PAGE) */}
      {/* ===================================================================== */}
      {roles.length > 0 && (
        <div
          className="results-position-card"
          style={{
            border: '2px solid #FF9900',
            boxShadow: '0 6px 20px rgba(255, 153, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Card Top Title */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #F3F4F6',
              paddingBottom: '16px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div style={{ flex: 1, minWidth: '240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span
                  style={{
                    backgroundColor: '#FFF7ED',
                    color: '#FF9900',
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: '3px 8px',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Candidate Intelligence
                </span>
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#171619', margin: '4px 0 2px 0', fontFamily: 'Syne, sans-serif' }}>
                How People Voted for Each Role
              </h2>
              <span style={{ fontSize: '13px', color: '#6B7280' }}>
                Inspect candidate voting distributions per academic cohort, gender, and student residence halls.
              </span>
            </div>

            {/* View Mode Switcher */}
            <div className="demographic-scroll-pills">
              {[
                { id: 'matrix', label: 'Matrix Table', icon: TableIcon },
                { id: 'stacked', label: 'Cohort Share', icon: BarChart2 },
                { id: 'cards', label: 'Candidate Cards', icon: LayoutGrid },
              ].map((m) => {
                const Icon = m.icon;
                const isActive = roleViewMode === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setRoleViewMode(m.id as RoleViewMode)}
                    className="demographic-pill-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      borderRadius: '9px',
                      backgroundColor: isActive ? '#171619' : 'transparent',
                      color: isActive ? '#FF9900' : '#4B5563',
                      fontWeight: 700,
                      fontSize: '12px',
                    }}
                  >
                    <Icon size={14} />
                    <span>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role Selector Buttons / Dropdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Select Role to Inspect:
              </div>

              {/* Dimension Switcher (Level, Gender, Hall) */}
              <div className="demographic-scroll-pills">
                {[
                  { id: 'level', label: 'By Level' },
                  { id: 'gender', label: 'By Gender' },
                  { id: 'hall', label: 'By Hall' },
                ].map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setRoleDimension(d.id as RoleDimension)}
                    className="demographic-pill-btn"
                    style={{
                      padding: '6px 14px',
                      borderRadius: '9px',
                      backgroundColor: roleDimension === d.id ? '#FF9900' : 'transparent',
                      color: roleDimension === d.id ? '#171619' : '#4B5563',
                      fontWeight: 700,
                      fontSize: '12px',
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Position Pills Bar */}
            <div className="demographic-role-pills">
              {roles.map((pos) => {
                const isSelected = (currentRole && currentRole.positionId === pos.positionId);
                return (
                  <button
                    key={pos.positionId}
                    onClick={() => setSelectedRoleId(pos.positionId)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '12px',
                      border: isSelected ? '1.5px solid #171619' : '1px solid #E5E7EB',
                      backgroundColor: isSelected ? '#171619' : '#FFFFFF',
                      color: isSelected ? '#FF9900' : '#4B5563',
                      fontWeight: 700,
                      fontSize: '13px',
                      fontFamily: 'Syne, sans-serif',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {pos.positionName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Render Candidate Demographic Breakdown for Selected Position */}
          {renderRoleBreakdown()}
        </div>
      )}
    </div>
  );
};
